import type {
  AuditIssueDTO,
  CreateAuditIssueDTO,
  UpdateAuditIssueDTO,
} from "@/modules/internal-audit/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Field, FormInput, FormRoot, FormTextarea } from "@devhop/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import * as RHF from "react-hook-form";
import { z } from "zod";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; name: string };
type UserItem = { id: string; name: string };
type AuditRecordItem = { id: string; name: string };

const AuditIssueFormSchema = z.object({
  auditRecordId: z.string().min(1, "ກະລຸນາເລືອກບັນທຶກການກວດສອບ"),
  auditRecordItemId: z.string().optional().nullable(),
  title: z.string().min(1, "ກະລຸນາປ້ອນຊື່ບັນຫາ"),
  description: z.string().min(1, "ກະລຸນາປ້ອນລາຍລະອຽດບັນຫາ"),
  rootCause: z.string().optional().nullable(),
  departmentId: z.string().min(1, "ກະລຸນາເລືອກພະແນກ"),
  severity: z.enum(["low", "medium", "high", "critical"]),
  assignedToPositionId: z.string().optional().nullable(),
  assignedToUserId: z.string().optional().nullable(),
  dueDate: z.string().optional().nullable(),
});

type AuditIssueFormValues = z.infer<typeof AuditIssueFormSchema>;

type AuditIssueFormProps = {
  initialValues?: Partial<AuditIssueDTO>;
  onSubmit: (values: CreateAuditIssueDTO | UpdateAuditIssueDTO) => void;
  submitting?: boolean;
};

export function AuditIssueForm({
  initialValues,
  onSubmit,
  submitting,
}: AuditIssueFormProps) {
  const methods = RHF.useForm<AuditIssueFormValues>({
    resolver: zodResolver(AuditIssueFormSchema),
    defaultValues: {
      auditRecordId: initialValues?.auditRecordId || "",
      auditRecordItemId: initialValues?.auditRecordItemId || null,
      title: initialValues?.title || "",
      description: initialValues?.description || "",
      rootCause: initialValues?.rootCause || null,
      departmentId: initialValues?.departmentId || "",
      severity: initialValues?.severity || "medium",
      assignedToPositionId: initialValues?.assignedToPositionId || null,
      assignedToUserId: initialValues?.assignedToUserId || null,
      dueDate: initialValues?.dueDate
        ? formatDateForInput(initialValues.dueDate)
        : null,
    },
  });

  const handleSubmit = (vals: AuditIssueFormValues) => {
    onSubmit({
      auditRecordId: vals.auditRecordId,
      auditRecordItemId: vals.auditRecordItemId || null,
      title: vals.title,
      description: vals.description,
      rootCause: vals.rootCause || null,
      departmentId: vals.departmentId,
      severity: vals.severity,
      assignedToPositionId: vals.assignedToPositionId || null,
      assignedToUserId: vals.assignedToUserId || null,
      dueDate: vals.dueDate ? parseISO(vals.dueDate) : null,
    });
  };

  return (
    <FormRoot<AuditIssueFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="min-w-0 space-y-4"
    >
      <FormInfiniteCombobox<AuditRecordItem>
        name="auditRecordId"
        label="ບັນທຶກການກວດສອບ"
        requiredMark
        queryKey={["audit-records", "lookup"]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/internal-audit/records`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set(
              "filters",
              JSON.stringify([
                {
                  field: "auditPlanName",
                  op: "contains",
                  value: args.search,
                },
              ]),
            );
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("offset", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.data) ? data.data : [];
          const total =
            typeof data.meta?.total === "number"
              ? data.meta.total
              : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return {
            items: items.map(
              (item: { id: string; auditPlanName: string | null }) => ({
                id: item.id,
                name: item.auditPlanName || `Record-${item.id.slice(0, 8)}`,
              }),
            ),
            nextPage,
          };
        }}
        preloadQueryFn={async (id) => {
          const response = await fetch(
            `${config.apiUrl}/internal-audit/records/${id}`,
            {
              credentials: "include",
            },
          );
          if (!response.ok) return null;
          const data = await response.json();
          return {
            id: data.id,
            name: data.auditPlanName || `Record-${data.id.slice(0, 8)}`,
          };
        }}
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກບັນທຶກການກວດສອບ"
      />

      <FormInput
        name="title"
        label="ຊື່ບັນຫາ"
        placeholder="ຕົວຢ່າງ: ບໍ່ປະຕິບັດຕາມ SOP"
        required
      />

      <FormTextarea
        name="description"
        label="ລາຍລະອຽດບັນຫາ"
        placeholder="ອະທິບາຍລາຍລະອຽດຂອງບັນຫາ..."
        required
        rows={4}
      />

      <FormTextarea
        name="rootCause"
        label="ສາເຫດ"
        placeholder="ອະທິບາຍສາເຫດຂອງບັນຫາ..."
        rows={3}
      />

      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        requiredMark
        queryKey={["departments", "issue-form"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/departments/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກພະແນກ"
      />

      <Field name="severity" label="ລະດັບຄວາມຮຸນແຮງ">
        <RHF.Controller
          control={methods.control}
          name="severity"
          render={({ field }) => (
            <SimpleSelect
              value={field.value}
              onValueChange={field.onChange}
              options={[
                { value: "low", label: "ເບົາ" },
                { value: "medium", label: "ກາງ" },
                { value: "high", label: "ສູງ" },
                { value: "critical", label: "ວິກິດ" },
              ]}
            />
          )}
        />
      </Field>

      <FormInfiniteCombobox<PositionItem>
        name="assignedToPositionId"
        label="ຕໍາແໜ່ງຜູ້ຮັບຜິດຊອບ"
        queryKey={["positions", "issue-form"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<UserItem>
        name="assignedToUserId"
        label="ພະນັກງານຜູ້ຮັບຜິດຊອບ"
        queryKey={["users", "issue-form"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກພະນັກງານ (ບໍ່ບັງຄັບ)"
        clearable
      />

      <Field name="dueDate" label="ວັນທີໝາຍ">
        <RHF.Controller
          control={methods.control}
          name="dueDate"
          render={({ field }) => (
            <input
              type="date"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value || null)}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          )}
        />
      </Field>

      <div className="sticky bottom-0 -mx-4 flex justify-end gap-2 border-t bg-background px-4 pt-4 pb-4">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
